<template>
  <div class="d-flex align-items-center whitespace-pre">
    <!-- Prefix -->
    <span v-if="hasValue && defaultPrefix">{{ defaultPrefix }}</span>

    <!-- Id column -->
    <router-link
      v-if="hasValue && defaultType === 'id'"
      v-b-tooltip.hover.ds250.v-dark.top="value"
      :to="detailRoute"
      class="font-monospace"
    >#{{ typeof value === 'string' ? value.substr(0, 8) : value }}</router-link>

    <!-- Image type's field -->
    <div
      v-else-if="hasValue && defaultType === 'image'"
    >
      <a
        :href="value"
        target="_blank"
      >
        <b-avatar
          variant="light"
          rounded="sm"
          :src="defaultThumbnail"
          size="40px"
        />
      </a>
    </div>

    <!-- Video type's field -->
    <div
      v-else-if="hasValue && defaultType === 'video'"
    >
      <a
        :href="value"
        target="_blank"
        class="position-relative"
      >
        <b-avatar
          variant="light"
          rounded="sm"
          :src="defaultThumbnail"
          size="40px"
        />

        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="currentColor"
          class="text-white position-absolute"
          style="z-index: 10; left: 50%; top: 50%; transform: translate(-50%, -50%); margin-top: 2px"
        ><path d="m380-300 280-180-280-180v360ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" /></svg>
      </a>
    </div>

    <!-- Images type's field -->
    <div
      v-else-if="hasValue && defaultType === 'images' && Array.isArray(value) && value.length"
      class="td-long"
    >
      <a
        v-for="(item, index) in value"
        :key="`list-images-${index}`"
        :href="item.image_url"
        target="_blank"
        class="mr-50 mb-1"
      >
        <b-avatar
          variant="light"
          rounded="sm"
          :src="item.thumbnail_url"
          size="40px"
        />
      </a>
    </div>

    <!-- Badges type's field -->
    <div
      v-else-if="hasValue && value.length && defaultType === 'badges'"
      class="d-flex gap-1"
    >
      <b-badge
        v-for="(item, index) in defaultBadges"
        :key="`badges-field-${index}`"
        :variant="_.get(map, item, 'secondary')"
        class="text-capitalize"
      >
        {{ $t(translationPrefix + item) }}
      </b-badge>
      <b-badge
        v-if="hiddenBadges.length > 0"
        v-b-tooltip.v-dark.top="{ title: hiddenBadges.join(', '), customClass: 'text-capitalize' }"
        class="text-capitalize"
      >
        + {{ hiddenBadges.length }} lainnya
      </b-badge>
    </div>

    <!-- Badge type's field -->
    <b-badge
      v-else-if="hasValue && defaultType === 'badge'"
      :variant="_.get(map, value, 'secondary')"
      class="text-capitalize"
    >
      <span v-if="text">{{ $t('item') }}</span>
      <span v-else>{{ $t(translationPrefix + value.toString()).replace(/_/g, ' ') }}</span>
    </b-badge>

    <!-- Color type's field -->
    <div
      v-else-if="hasValue && defaultType === 'color'"
      class="d-flex align-items-center text-nowrap"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 -960 960 960"
        width="24px"
        fill="currentColor"
        :style="{ color: value }"
      ><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Z" /></svg>

      <span class="text-capitalize ml-25">{{ $t(value.toString()).replace(/_/g, ' ') }}</span>
    </div>

    <!-- Dot type's field -->
    <div
      v-else-if="hasValue && defaultType === 'dot'"
      class="d-flex align-items-center text-nowrap"
    >
      <div :class="`text-${_.get(map, value, 'secondary')}`">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="8"
            cy="6.5"
            r="5.5"
          />
        </svg>
      </div>

      <span class="text-capitalize ml-25">{{ $t(value.toString()).replace(/_/g, ' ') }}</span>
    </div>

    <!-- Profile type's field -->
    <!-- Suitable for general profile -->
    <!-- e.g. User profile, Product profile -->
    <div
      v-else-if="(hasValue || image) && defaultType === 'profile'"
      class="d-flex align-items-center line-clamp-1 whitespace-normal w-max-content"
    >
      <!-- Avatar -->
      <a
        :href="image"
        target="_blank"
      >
        <b-avatar
          size="32px"
          :src="defaultProfileImage"
        />
      </a>

      <!-- Name -->
      <div class="ml-75">
        <a
          v-if="hasValue && defaultUrl"
          :href="defaultUrl"
          target="_blank"
          class="line-clamp-1"
          :title="value"
        >{{ value }}</a>
        <span
          v-else-if="hasValue"
          class="line-clamp-1"
          :title="value"
        >{{ value }}</span>
        <i
          v-else
          class="text-muted"
        >Tanpa nama</i>

        <span
          v-if="subtitle"
          class="text-muted line-clamp-1 s-12"
        >{{ subtitle }}</span>
      </div>
    </div>

    <!-- Profiles type's field -->
    <b-avatar-group
      v-else-if="hasValue && defaultType === 'profiles' && Array.isArray(value) && value.length"
      size="32px"
    >
      <b-avatar
        v-for="(item, index) in defaultBadges"
        :key="`profile-${index}`"
        v-b-tooltip.hover
        :title="item.name"
        :src="item.image_url"
        size="32px"
      />
      <b-avatar
        v-if="hiddenBadges.length > 0"
        v-b-tooltip.hover
        :title="hiddenBadges.map(i => i.name).join(', ')"
        :text="`+${hiddenBadges.length}`"
        size="32px"
      />
    </b-avatar-group>

    <!-- Phone type's field -->
    <a
      v-else-if="hasValue && defaultType === 'phone'"
      :href="`https://wa.me/${value}`"
      target="_blank"
    >{{ value | phone }}</a>

    <!-- Datetime type's field -->
    <span
      v-else-if="hasValue && defaultType === 'datetime'"
      class="text-capitalize"
    >{{ value | datetime }}</span>

    <!-- Datetime type's field -->
    <span
      v-else-if="hasValue && defaultType === 'date'"
      class="text-capitalize"
    >{{ value | date }}</span>

    <!-- Email format -->
    <a
      v-else-if="hasValue && defaultType === 'email'"
      :href="`mailto:${value}`"
    >{{ value }}</a>

    <!-- Email format -->
    <a
      v-else-if="hasValue && defaultType === 'whatsapp'"
      :href="`https://wa.me/${value
        .replace(/\D/g, '')
        .replace(/^(0+)/, '')
        .replace(/^(\+62|0)/, '62')}`"
      target="_blank"
    >{{ value }}</a>

    <!-- Rating format -->
    <div
      v-else-if="hasValue && defaultType === 'rating'"
      class="d-flex align-items-center gap-1"
    >
      {{ value }}
      <feather-icon
        icon="StarIcon"
        class="text-warning"
        size="16"
      />
    </div>

    <!-- Country field -->
    <CountryField
      v-else-if="hasValue && defaultType === 'country'"
      :code="value"
    />

    <!-- Array format -->
    <div
      v-else-if="hasValue && defaultType === 'array' && Array.isArray(value)"
      class="d-flex align-items-center"
      :title="value"
    >
      <span
        v-if="value.length"
        class="text-truncate"
        :style="{ maxWidth: value.length > 1 ? '150px' : '260px' }"
      >{{ value[0] }}</span>
      <span v-if="value.length > 1">, dan {{ value.length - 1 }} lainnya.</span>
    </div>

    <!-- Url -->
    <a
      v-else-if="hasValue && defaultType === 'url'"
      v-b-tooltip.hover.ds250.v-dark.top="value"
      :href="defaultUrl"
      target="_blank"
      class="text-truncate d-inline-block"
      :title="value"
      style="max-width: 260px"
    >{{ shortUrl }}</a>

    <!-- HTML format -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div
      v-else-if="hasValue && defaultType === 'html'"
      class="single-line-html"
      v-html="value"
    />

    <!-- Text format -->
    <span
      v-else-if="hasValue && defaultType === 'text'"
    >{{ value }}</span>

    <!-- Number format -->
    <span
      v-else-if="hasValue && typeof value === 'number'"
    >{{ value | number }}</span>

    <!-- Boolean -->
    <div v-else-if="hasValue && typeof value === 'boolean'">
      <feather-icon
        v-if="value"
        icon="CheckCircleIcon"
        class="text-success"
        size="20"
      />
      <feather-icon
        v-else
        icon="XCircleIcon"
        class="text-danger"
        size="20"
      />
    </div>

    <!-- Default -->
    <div
      v-else-if="hasValue && typeof value !== 'object'"
      class="line-clamp-1 d-block"
    >{{ value }}</div>

    <!-- Empty -->
    <div
      v-else
      class="text-muted s-12 py-25"
    >●</div>

    <!-- Suffix -->
    <span v-if="hasValue && defaultSuffix">{{ defaultSuffix }}</span>

    <!-- Copy text -->
    <!-- should have value, array should have item(s) -->
    <!-- or not an array but have value-->
    <div
      v-if="showCopy && hasValue && (value.length || typeof value !== 'object') && defaultType !== 'images'"
      class="position-relative"
    >
      <b-button
        ref="btn-copy"
        variant="flat-dark"
        class="btn-icon btn-copy ml-50"
        size="sm"
        @click.prevent="copyText(value, $event)"
      >
        <feather-icon
          icon="CopyIcon"
          class="pointer-events-none"
          size="16"
        />
      </b-button>
      <!-- Tooltip -->
      <b-tooltip
        ref="tooltip-copy"
        :target="() => $refs['btn-copy']"
        title="Copied!"
        placement="top"
        no-fade
        disabled
      />
    </div>
  </div>
</template>

<script>
import { copy } from '@/helper/clipboard'
import CountryField from '@/layouts/components/v2/CountryField.vue'
import {
  BBadge,
  BButton,
  BAvatar,
  BAvatarGroup,
  BTooltip,
} from 'bootstrap-vue'
import goldIcon from '@/assets/images/icons/gold.svg'

export default {
  components: {
    BBadge,
    BButton,
    CountryField,
    BAvatar,
    BAvatarGroup,
    BTooltip,
  },
  props: {
    value: {
      type: [String, Number, Array, Boolean],
      default: '',
    },
    prefix: {
      type: [String, Object],
      default: '',
    },
    suffix: {
      type: [String, Object],
      default: '',
    },
    type: {
      type: [String, Object],
      default: '',
    },
    map: {
      type: Object,
      default: () => {},
    },
    image: {
      type: String,
      default: '',
    },
    thumbnail: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => {},
    },
    url: {
      type: [String, Object],
      default: '',
    },
    hasDetail: {
      type: Boolean,
      default: false,
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: [Number, Boolean, Object],
      default: 2,
    },
    context: {
      type: String,
      default: 'index',
    },
    translationPrefix: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      goldIcon,
    }
  },
  computed: {
    effectiveLimit() {
      if (typeof this.limit === 'object' && this.limit !== null) {
        return this.limit[this.context]
      }
      return this.limit
    },
    defaultThumbnail() {
      return this.thumbnail || this.value
    },
    defaultProfileImage() {
      return this.thumbnail || this.image
    },
    // get only first item if there are more than 2 items
    defaultBadges() {
      if (!this.value) {
        return []
      }

      if (this.effectiveLimit === false || this.value.length <= this.effectiveLimit) {
        return this.value
      }

      return this.value.slice(0, this.effectiveLimit)
    },
    // get dynamic prefix
    defaultPrefix() {
      if (!this.prefix) {
        return undefined
      }

      if (typeof this.prefix === 'string') {
        return this.prefix
      }

      if (typeof this.prefix !== 'object') {
        return undefined
      }

      // dynamic value
      return this.prefix.callback(this.data)
    },
    // get dynamic suffix
    defaultSuffix() {
      if (!this.suffix) {
        return undefined
      }

      if (typeof this.suffix === 'string') {
        return this.suffix
      }

      if (typeof this.suffix !== 'object') {
        return undefined
      }

      // dynamic value
      return this.suffix.callback(this.data)
    },
    // get dynamic type
    defaultType() {
      if (!this.type) {
        return undefined
      }

      if (typeof this.type === 'string') {
        return this.type
      }

      if (typeof this.type !== 'object') {
        return undefined
      }

      // dynamic value
      return this.type.callback(this.data)
    },
    defaultUrl() {
      if (!this.url && (this.value.includes('http') || this.value.includes('www') || this.value.includes('.com'))) {
        return this.value
      }

      if (!this.url) {
        return undefined
      }

      if (typeof this.url === 'string') {
        return this.url
      }

      if (typeof this.url !== 'object') {
        return undefined
      }

      // dynamic value
      return this.url.callback(this.data)
    },
    shortUrl() {
      if (!this.hasValue) return ''
      const str = this.value.toString()
      // Remove protocol and leading www.
      const cleaned = str.replace(/^https?:\/\//, '').replace(/^www\./, '')
      // If not too long just return cleaned
      if (cleaned.length <= 40) return cleaned
      // Keep first 40 chars
      return `${cleaned.slice(0, 40)}…`
    },
    // get only first item if there are more than 2 items
    hiddenBadges() {
      if (!this.value || this.effectiveLimit === false || this.value.length <= this.effectiveLimit) {
        return []
      }
      return this.value.slice(this.effectiveLimit)
    },
    detailRoute() {
      // if already in detail route
      if (this.$route.path.includes(this.value)) {
        return this.$route.path
      }

      if (this.hasDetail) {
        // return detail route
        return `${this.$route.path}/${this.value}`
      }

      // open popup instead
      return `${this.$route.path}#${this.value}`
    },
    // using if(value) and !_.isEmpty for number 0 will return false
    hasValue() {
      return this.value !== undefined && this.value !== null && this.value !== ''
    },
  },
  methods: {
    copyText(val) {
      let textToCopy = val

      // Handle profiles array - extract names
      if (this.defaultType === 'profiles' && Array.isArray(val)) {
        textToCopy = val.map(item => item.name || item.title || item.label || 'Unknown').join(', ')
      } else if (Array.isArray(val)) {
        // Handle other arrays
        textToCopy = val.join(', ')
      } else if (typeof val === 'object' && val !== null) {
        // Handle objects
        textToCopy = val.name || val.title || val.label || JSON.stringify(val)
      } else {
        // Default conversion to string
        textToCopy = val.toString()
      }

      copy(textToCopy)
        .then(() => {
          // show tooltip only when success
          this.$refs['tooltip-copy'].$emit('open')

          // hide tooltip after half a second
          setTimeout(() => {
            this.$refs['tooltip-copy'].$emit('close')
          }, 500)
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.b-table tbody {
  td {
    .btn-copy {
      z-index: 1;
      position: absolute;
      top: 50%;
      transform: translateY(-50%) scale(0);
      opacity: 0;
      transition: opacity 0.1s ease-in-out, transform 0s linear;
      transition-delay: 0s;
    }

    &:hover {
      .btn-copy {
        transform: translateY(-50%) scale(1);
        opacity: 1; // do not use 100%, in production will changed to 1%
        transition-delay: 0.5s;
      }
    }
  }
}

.single-line-html {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;

  ::v-deep > * {
    margin-bottom: 0;
  }
}

// Fix avatar group to ensure circular shape
::v-deep .b-avatar-group .b-avatar {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  border-radius: 50% !important;

  .b-avatar-img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
}
</style>
